/**
 * Omnisearch and subsidiary list API schemas.
 */

import { z } from 'zod';
import type {
    CursorSearchRequest,
    ListDirectOrganizationUsersPageQuery,
    OmnisearchSearchRequest,
    OrganizationOmnisearchSearchRequest,
    OpaqueCursorSearchRequest,
    SearchFieldPredicate,
    SearchFieldPredicateOperator,
    SearchFieldPredicateValue,
    SearchJsonPathPredicate,
    SearchJsonPathPredicateOperator,
    UserOmnisearchSearchRequest,
} from '../omnisearch.types';
import {
    OrganizationOmnisearchListItemSchema,
} from './organization.schemas';
import {
    UserProfileDetailedPublicResponseSchema,
} from './user.schemas';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import {
    AuthorizationOpaqueCursorQuerySchema,
    AuthorizationPaginationCursorQuerySchema,
    AuthorizationPaginationLimitQuerySchema,
} from './authorization.schemas';
import { JsonValueSchema } from './common.schemas';

export const CursorPageQuerySchema = z.object({
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationPaginationCursorQuerySchema,
});

export type CursorPageQuery = z.infer<typeof CursorPageQuerySchema>;

export const OpaqueCursorPageQuerySchema = z.object({
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationOpaqueCursorQuerySchema,
});

export type OpaqueCursorPageQuery = z.infer<typeof OpaqueCursorPageQuerySchema>;

const IncludeDescendantsQuerySchemaBase: z.ZodType<boolean | undefined> = z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((value: boolean | 'true' | 'false'): boolean => value === true || value === 'true')
    .optional();
export const IncludeDescendantsQuerySchema: z.ZodType<boolean | undefined> =
    IncludeDescendantsQuerySchemaBase;

export const ListDirectOrganizationUsersPageQuerySchema: z.ZodType<
    ListDirectOrganizationUsersPageQuery
> = z.object({
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationOpaqueCursorQuerySchema,
    include_descendants: IncludeDescendantsQuerySchema,
});

export const SearchTermsSchema: z.ZodType<string[] | undefined> = z
    .array(z.string().trim().min(1))
    .optional();

export const SearchFieldPredicateOperatorSchema: z.ZodType<SearchFieldPredicateOperator> =
    z.enum(['eq', 'in']);

export const SearchFieldPredicateValueSchema: z.ZodType<SearchFieldPredicateValue> =
    z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const SearchFieldPredicateSchema: z.ZodType<SearchFieldPredicate> = z
    .object({
        field: z.string().trim().min(1),
        operator: SearchFieldPredicateOperatorSchema,
        value: SearchFieldPredicateValueSchema.optional(),
        values: z.array(SearchFieldPredicateValueSchema).min(1).optional(),
    })
    .strict()
    .superRefine((predicate: SearchFieldPredicate, ctx) => {
        if (predicate.operator === 'eq') {
            if (predicate.value === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'eq field predicates require value',
                    path: ['value'],
                });
            }
            if (predicate.values !== undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'eq field predicates must not include values',
                    path: ['values'],
                });
            }
            return;
        }
        if (predicate.values === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'in field predicates require values',
                path: ['values'],
            });
        }
        if (predicate.value !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'in field predicates must not include value',
                path: ['value'],
            });
        }
    });

export const SearchFieldPredicatesSchema: z.ZodType<SearchFieldPredicate[] | undefined> =
    z.array(SearchFieldPredicateSchema).max(25).optional();

export const SearchJsonPathPredicateOperatorSchema: z.ZodType<SearchJsonPathPredicateOperator> =
    z.enum(['eq', 'contains']);

export const SearchJsonPathPredicateSchema: z.ZodType<SearchJsonPathPredicate> = z
    .object({
        field: z.string().trim().min(1),
        path: z.array(z.string().trim().min(1)).min(1).max(25),
        operator: SearchJsonPathPredicateOperatorSchema,
        value: JsonValueSchema,
    })
    .strict();

export const SearchJsonPathPredicatesSchema: z.ZodType<
    SearchJsonPathPredicate[] | undefined
> = z.array(SearchJsonPathPredicateSchema).max(25).optional();

export const SearchScopeOrganizationUuidSchema: z.ZodType<string | undefined> = z
    .string()
    .uuid()
    .optional();

export const CursorSearchRequestSchema: z.ZodType<CursorSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationPaginationCursorQuerySchema,
        field_predicates: SearchFieldPredicatesSchema,
        json_path_predicates: SearchJsonPathPredicatesSchema,
    })
    .strict();

export const OpaqueCursorSearchRequestSchema: z.ZodType<OpaqueCursorSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationOpaqueCursorQuerySchema,
        field_predicates: SearchFieldPredicatesSchema,
        json_path_predicates: SearchJsonPathPredicatesSchema,
    })
    .strict();

export const OmnisearchSearchRequestSchema: z.ZodType<OmnisearchSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationPaginationCursorQuerySchema,
        field_predicates: SearchFieldPredicatesSchema,
        json_path_predicates: SearchJsonPathPredicatesSchema,
    })
    .strict();

export const UserOmnisearchQuerySchema = z.object({
    q: z.string().trim().optional(),
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationOpaqueCursorQuerySchema,
    organization_uuid: SearchScopeOrganizationUuidSchema,
});

export type UserOmnisearchQuery = z.infer<typeof UserOmnisearchQuerySchema>;

export const UserOmnisearchSearchRequestSchema: z.ZodType<UserOmnisearchSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationOpaqueCursorQuerySchema,
        organization_uuid: SearchScopeOrganizationUuidSchema,
    })
    .strict();

const OrganizationCapacityFilterQueryValueSchema: z.ZodType<true | undefined> = z
    .union([z.literal(true), z.enum(['true'])])
    .optional()
    .transform((value: true | 'true' | undefined): true | undefined => {
        if (value === undefined) {
            return undefined;
        }
        return true;
    });

export const OrganizationCapacityFilterQuerySchema: z.ZodObject<{
    is_iso: z.ZodType<true | undefined>;
    is_merchant: z.ZodType<true | undefined>;
    is_customer: z.ZodType<true | undefined>;
}> = z.object({
    is_iso: OrganizationCapacityFilterQueryValueSchema,
    is_merchant: OrganizationCapacityFilterQueryValueSchema,
    is_customer: OrganizationCapacityFilterQueryValueSchema,
});

export const OrganizationOmnisearchSearchRequestSchema: z.ZodType<
    OrganizationOmnisearchSearchRequest
> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationOpaqueCursorQuerySchema,
        is_iso: OrganizationCapacityFilterQueryValueSchema,
        is_merchant: OrganizationCapacityFilterQueryValueSchema,
        is_customer: OrganizationCapacityFilterQueryValueSchema,
        organization_uuid: SearchScopeOrganizationUuidSchema,
        include_descendants: z.boolean().optional(),
        show_deleted: z.coerce.boolean().optional(),
    })
    .strict();

export const OrganizationOmnisearchPageResponseSchema = z.object({
    organizations: z.array(OrganizationOmnisearchListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const UserOmnisearchPageResponseSchema = z.object({
    users: z.array(UserProfileDetailedPublicResponseSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const ListSubsidiaryIsosPageResponseSchema = z.object({
    isos: z.array(OrganizationOmnisearchListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const ListSubsidiaryMerchantsPageResponseSchema = z.object({
    merchants: z.array(OrganizationOmnisearchListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const ListDirectOrganizationUsersPageResponseSchema = z.object({
    users: z.array(UserProfileDetailedPublicResponseSchema),
    pagination: OpaqueCursorPaginationSchema,
});
